import type { ComponentType, ElementType, ReactNode } from 'react';
import type { StreamdownProps } from 'streamdown';

import { componentMappings as markdownComponentMappings } from '../../markdown/Markdown.js';
import type { AiResponseCodeBlockProps } from '../ai-response-types.js';
import { useAiResponseBlock } from '../AiResponseBlockContext.js';
import { AiResponseTable } from '../components/AiResponseTable.js';

const FENCE_LANGUAGE_PREFIX = 'language-';

/**
 * Streamdown's component map type.
 */
type StreamdownComponents = NonNullable<StreamdownProps['components']>;

/**
 * Map of fence language identifiers to custom renderers provided by the consumer.
 */
type CodeBlockRenderers = Record<
  string,
  ComponentType<AiResponseCodeBlockProps>
>;

const DefaultCode = markdownComponentMappings.code as ElementType;

/**
 * Extracts the language identifier from a code element's className (e.g. `"language-ts"` → `"ts"`).
 * @param className - The className value from the code element.
 * @returns the language string, or undefined if no language prefix is present.
 */
function getFenceLanguage(className: unknown): string | undefined {
  if (
    typeof className !== 'string' ||
    !className.includes(FENCE_LANGUAGE_PREFIX)
  ) {
    return undefined;
  }
  return className.slice(
    className.indexOf(FENCE_LANGUAGE_PREFIX) + FENCE_LANGUAGE_PREFIX.length,
  );
}

/**
 * Normalizes a code element's children to a plain string, stripping the trailing newline
 * that markdown parsers append to fenced blocks.
 * @param children - The children of the code element.
 * @returns the normalised code string.
 */
function toCode(children: ReactNode): string {
  return (
    Array.isArray(children) ? children.join('') : String(children ?? '')
  ).replace(/\n$/, '');
}

/**
 * Renders a fenced code block using a consumer-provided renderer for the given language.
 */
function CustomCodeBlock({
  Renderer,
  language,
  children,
}: {
  Renderer: ComponentType<AiResponseCodeBlockProps>;
  children: ReactNode;
  language: string;
}) {
  const { isComplete } = useAiResponseBlock();

  return (
    <Renderer
      code={toCode(children)}
      language={language}
      isComplete={isComplete}
    />
  );
}

function buildStreamdownComponents(
  customCodeBlockRenderers?: CodeBlockRenderers,
): StreamdownComponents {
  // Copying to a null-prototype object keeps a fence language like `toString` from
  // resolving an inherited Object.prototype member instead of falling back to the default.
  const renderers: CodeBlockRenderers = Object.assign(
    Object.create(null) as CodeBlockRenderers,
    customCodeBlockRenderers,
  );

  return {
    ...markdownComponentMappings,
    code: ({ children, node, ...restProps }) => {
      const isSingleLine =
        node?.position?.start !== undefined &&
        node.position.end !== undefined &&
        node.position.start.line === node.position.end.line;
      const language = getFenceLanguage(restProps.className);
      // While a fence is streaming, the opening line can arrive before any content, so the
      // block still spans a single line. Its language keeps it apart from real inline code.
      const isInline = isSingleLine && language === undefined;

      if (language !== undefined) {
        const Renderer = renderers[language];

        if (Renderer) {
          return (
            <CustomCodeBlock Renderer={Renderer} language={language}>
              {children}
            </CustomCodeBlock>
          );
        }
      }

      return (
        <DefaultCode inline={isInline} node={node} {...restProps}>
          {children}
        </DefaultCode>
      );
    },
    table: AiResponseTable,
    // The markdownComponentMappings are typed for react-markdown, but they're compatible with
    // Streamdown so we can cast them here to avoid unnecessary duplication of the mapping logic.
  } as StreamdownComponents;
}

const defaultStreamdownComponents = buildStreamdownComponents();

/**
 * Builds the component mapping for Streamdown. It starts from the markdown mappings we
 * have for react-markdown and wraps the code component so that it receives whether the
 * code is inline or a block, and so that a fenced block whose language a consumer
 * registered a renderer for is handed over to that renderer.
 * @internal
 */
export const getStreamdownComponents = (
  customCodeBlockRenderers?: CodeBlockRenderers,
): StreamdownComponents =>
  customCodeBlockRenderers
    ? buildStreamdownComponents(customCodeBlockRenderers)
    : defaultStreamdownComponents;
